import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  GameController, Heart, ShoppingBag, Star, Users, ChartBar,
  TrendUp, Backpack, GraduationCap, PuzzlePiece, UserPlus
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  owned: '#10B981', favorite: '#F59E0B', wishlist: '#9D4CDD',
  interested: '#3B82F6', played: '#06B6D4', mastered: '#EC4899',
  trading: '#F97316', borrowed: '#8B5CF6', no_longer_owned: '#6B7280',
};

export default function AdminCollections() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin-console/collections/analytics`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div data-testid="admin-collections">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Exo 2' }}>Collection Analytics</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-white/[0.03] rounded-lg animate-pulse" />)}
      </div>
    </div>
  );

  if (!data) return <p className="text-[#555]">Failed to load analytics</p>;

  const maxOwned = data.top_owned?.[0]?.count || 1;
  const maxWish = data.top_wishlisted?.[0]?.count || 1;

  return (
    <div data-testid="admin-collections">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Exo 2' }}>Collection Analytics</h2>
        <p className="text-[13px] text-[#525252] mt-1">Game collection insights across all users</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <KPI icon={GameController} label="Collection Entries" value={data.total_entries} color="#06B6D4" />
        <KPI icon={Users} label="Collectors" value={data.total_collectors} color="#007AFF" />
        <KPI icon={ChartBar} label="Avg Collection" value={data.avg_collection_size} color="#10B981" />
        <KPI icon={PuzzlePiece} label="Games in DB" value={data.total_games_in_db} color="#F59E0B" />
        <KPI icon={UserPlus} label="User-Submitted" value={data.user_submitted_games} color="#9D4CDD" />
        <KPI icon={Backpack} label="Will Bring" value={data.willing_to_bring} color="#10B981" />
        <KPI icon={GraduationCap} label="Will Teach" value={data.willing_to_teach} color="#F59E0B" />
        <KPI icon={TrendUp} label="Added Today" value={data.daily_growth?.[0]?.added || 0} color="#EC4899" />
      </div>

      {/* Status Distribution */}
      <div className="bg-[#111111] border border-[#262626] rounded-lg p-5 mb-6">
        <p className="text-sm font-semibold text-white mb-4">Status Distribution</p>
        <div className="flex flex-wrap gap-3">
          {data.status_distribution?.map(s => (
            <div key={s.status} className="flex items-center gap-2 bg-[#1A1A1A] rounded-lg px-3 py-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[s.status] || '#525252' }} />
              <span className="text-[12px] text-[#A3A3A3] capitalize">{s.status.replace(/_/g, ' ')}</span>
              <span className="text-[12px] text-white font-semibold">{s.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Top Owned */}
        <div className="bg-[#111111] border border-[#262626] rounded-lg p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <GameController size={16} weight="duotone" className="text-[#10B981]" /> Top Owned Games
          </p>
          <div className="space-y-2">
            {data.top_owned?.map((g, i) => (
              <div key={g.game_id} className="flex items-center gap-3" data-testid={`top-owned-${i}`}>
                <span className="w-5 text-[11px] text-[#525252] font-mono text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] text-white truncate">{g.game_name}</p>
                    {g.user_submitted && <span className="text-[8px] px-1 py-0.5 rounded bg-[#9D4CDD]/10 text-[#9D4CDD]">custom</span>}
                  </div>
                  <div className="h-1 bg-[#1A1A1A] rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-[#10B981] rounded-full" style={{ width: `${(g.count / maxOwned) * 100}%` }} />
                  </div>
                </div>
                <span className="text-[12px] text-[#A3A3A3] font-mono w-8 text-right">{g.count}</span>
              </div>
            ))}
            {(!data.top_owned || data.top_owned.length === 0) && <p className="text-[#525252] text-xs">No data</p>}
          </div>
        </div>

        {/* Top Wishlisted */}
        <div className="bg-[#111111] border border-[#262626] rounded-lg p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <ShoppingBag size={16} weight="duotone" className="text-[#9D4CDD]" /> Top Wishlisted Games
          </p>
          <div className="space-y-2">
            {data.top_wishlisted?.map((g, i) => (
              <div key={g.game_id} className="flex items-center gap-3" data-testid={`top-wishlist-${i}`}>
                <span className="w-5 text-[11px] text-[#525252] font-mono text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] text-white truncate">{g.game_name}</p>
                    {g.user_submitted && <span className="text-[8px] px-1 py-0.5 rounded bg-[#9D4CDD]/10 text-[#9D4CDD]">custom</span>}
                  </div>
                  <div className="h-1 bg-[#1A1A1A] rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-[#9D4CDD] rounded-full" style={{ width: `${(g.count / maxWish) * 100}%` }} />
                  </div>
                </div>
                <span className="text-[12px] text-[#A3A3A3] font-mono w-8 text-right">{g.count}</span>
              </div>
            ))}
            {(!data.top_wishlisted || data.top_wishlisted.length === 0) && <p className="text-[#525252] text-xs">No data</p>}
          </div>
        </div>

        {/* Top Favorites */}
        <div className="bg-[#111111] border border-[#262626] rounded-lg p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Heart size={16} weight="duotone" className="text-[#F59E0B]" /> Top Favorites
          </p>
          <div className="space-y-2">
            {data.top_favorites?.map((g, i) => (
              <div key={g.game_id} className="flex items-center gap-3" data-testid={`top-fav-${i}`}>
                <span className="w-5 text-[11px] text-[#525252] font-mono text-right">{i + 1}</span>
                <p className="text-[13px] text-white flex-1 truncate">{g.game_name}</p>
                <span className="text-[12px] text-[#A3A3A3] font-mono">{g.count}</span>
              </div>
            ))}
            {(!data.top_favorites || data.top_favorites.length === 0) && <p className="text-[#525252] text-xs">No data</p>}
          </div>
        </div>

        {/* Daily Growth */}
        <div className="bg-[#111111] border border-[#262626] rounded-lg p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <TrendUp size={16} weight="duotone" className="text-[#EC4899]" /> 7-Day Growth
          </p>
          <div className="space-y-2">
            {data.daily_growth?.map((d, i) => {
              const maxDay = Math.max(...(data.daily_growth || []).map(x => x.added), 1);
              return (
                <div key={d.date} className="flex items-center gap-3" data-testid={`growth-${i}`}>
                  <span className="w-16 text-[11px] text-[#525252] font-mono">{d.date.slice(5)}</span>
                  <div className="flex-1 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div className="h-full bg-[#EC4899] rounded-full transition-all" style={{ width: `${(d.added / maxDay) * 100}%` }} />
                  </div>
                  <span className="text-[12px] text-[#A3A3A3] font-mono w-6 text-right">{d.added}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#111111] border border-[#262626] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} weight="duotone" style={{ color }} />
        <span className="text-[10px] text-[#525252] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-white">{value}</p>
    </div>
  );
}
